/**
 * Apple may omit `credential.email` on later sign-ins; the relay or real address
 * can still appear in the identity token JWT `email` claim (first authorization).
 *
 * Decode payload only (no signature verification) — the backend still owns trust.
 * Use UTF-8 decoding: JWT payloads are UTF-8 JSON; the old per-byte decodeURIComponent
 * path often threw or mis-parsed when non-ASCII appeared in other claims, hiding email.
 */
export function getEmailFromAppleIdentityToken(
  identityToken: string | null | undefined
): string | null {
  if (!identityToken || typeof identityToken !== 'string') return null;
  const parts = identityToken.split('.');
  if (parts.length < 2) return null;
  try {
    const segment = parts[1];
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(pad);
    if (typeof atob !== 'function') return null;
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i) & 0xff;
    }
    const jsonStr =
      typeof TextDecoder !== 'undefined'
        ? new TextDecoder('utf-8', { fatal: false }).decode(bytes)
        : binary;
    const payload = JSON.parse(jsonStr) as { email?: string | null };
    const email =
      typeof payload.email === 'string' && payload.email.includes('@')
        ? payload.email.trim()
        : null;
    return email || null;
  } catch {
    return null;
  }
}
